import { isNotNil } from "client/Misc/utils";
import { Mark, MarkType, Node } from "prosemirror-model";
import { EditorState } from "prosemirror-state";

export function isMarkActive(
	state: EditorState,
	mark: MarkType
): boolean {
	const {
		selection: {
			from,
			$from,
			to,
			empty
		},
		storedMarks,
		doc
	} = state;

	if (empty) {
		return isNotNil(mark.isInSet(storedMarks || $from.marks()));
	}

	return doc.rangeHasMark(
		from,
		to,
		mark
	);
}

export function getNodesWithPositionFromSelection(
	state: EditorState,
	filter: {
		markType?: MarkType;
	} = {}
): readonly NodeWithPosition[] {
	const {
		selection: {
			from,
			to
		},
		doc
	} = state;

	const {
		markType
	} = filter;

	const result: NodeWithPosition[] = [];
	doc.nodesBetween(
		from,
		to,
		(node: Node, position: number): void => {
			result.push({ node, position });
		}
	);

	if (markType) {
		return result.filter(x => x.node.marks.some(m => m.type === markType));
	}

	return result;
}

export function getTextFromSelection(state: EditorState): string {
	const {
		selection: {
			from,
			to,
			empty
		},
		doc
	} = state;

	if (empty) {
		return "";
	}

	return doc.textBetween(from, to, " ");
}

export type NodeWithPosition = {
	node: Node;
	position: number;
};

export function getUnanimousValueFromSelection<TValue>(
	editorState: EditorState,
	markType: MarkType,
	getValueFromMark: (mark: Mark) => TValue,
	defaultValue: TValue,
	areEqual: (value1: TValue, value2: TValue) => boolean
): TValue | undefined {
	const {
		selection: {
			$from,
			empty
		},
		storedMarks
	} = editorState;

	const getValueFromMarks = (marks: readonly Mark[]): TValue => {
		const relevantMark: Mark | undefined = marks.find(m => m.type === markType);
		if (!relevantMark) {
			return defaultValue;
		}

		return getValueFromMark(relevantMark);
	};

	if (empty) {
		return getValueFromMarks(storedMarks || $from.marks());
	}

	const nodesFromSelection: readonly NodeWithPosition[] = getNodesWithPositionFromSelection(
		editorState,
		{
			markType: markType
		}
	);

	if (nodesFromSelection.length === 0) {
		return defaultValue;
	}

	const allMarkValues: TValue[] = nodesFromSelection
		.map(n => getValueFromMarks(n.node.marks));

	const [firstMarkValue] = allMarkValues;
	const isUnanimous: boolean = allMarkValues.every(s =>	areEqual(firstMarkValue, s));

	if (isUnanimous) {
		return firstMarkValue;
	}

	return undefined;
}